// components/FarmerSelect.tsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import FarmerInfoForModal from "Common/FarmerInfoForModal";
import FarmerIdentityInfo from "Common/Components/FarmerIdentityInfo";
import { DEFAULT_CROP_YEAR_ID } from "Common/constants/cropYear";

interface Farmer {
  id: number;
  farmer_id: number;
  farmer_name: string;
}

interface FarmerSelectProps {
  selectedFarmer: any;
  onFarmerChange: (farmer: any) => void;
  isEdit: boolean;
  cropyearID?: number;
  commitID: any;
  setCommitID: (data: any) => void;
}

const FarmerSelect: React.FC<FarmerSelectProps> = ({
  selectedFarmer,
  onFarmerChange,
  isEdit,
  commitID,
  setCommitID,
  cropyearID = DEFAULT_CROP_YEAR_ID,
}) => {
  const [farmersList, setFarmersList] = useState<Farmer[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [isLoadingBtnModal, setIsLoadingBtnModal] = useState<boolean>(false);
  const [selectedFarmerInfo, setSelectedFarmerInfo] = useState<any>({
    amount_of_land: "",
    village_name: "",
    date_set: "",
  });

  useEffect(() => {
    const fetchFarmers = async () => {
      try {
        let url = `https://api.havirkesht.ir/commitment/?crop_year_id=${cropyearID}&sort_by=updated_at&sort_order=desc`;
        if (searchInput) {
          url += `&search=${encodeURIComponent(searchInput)}`;
        }

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("app_atkey")}`,
          },
        });

        setFarmersList(response.data?.items || []);
      } catch (error) {
        console.error("Error fetching farmers data:", error);
      }
    };

    fetchFarmers();
  }, [searchInput, cropyearID]);

  useEffect(() => {
    const getFarmerInfo = async () => {
      try {
        if (commitID) {
          setIsLoadingBtnModal(true);
          const res = await axios.get(
            `https://api.havirkesht.ir/commitment/${Number(commitID)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("app_atkey")}`,
              },
            }
          );

          if (res?.data.amount_of_land) {
            setSelectedFarmerInfo({
              amount_of_land: res?.data?.amount_of_land || "",
              village_name: res?.data?.village_name || "",
              date_set: res?.data?.date_set || "",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching farmer info:", error);
      } finally {
        setIsLoadingBtnModal(false);
      }
    };
    getFarmerInfo();
  }, [commitID]);

  const handleChange = (selectedOption: any) => {
    onFarmerChange(selectedOption?.data || null);
    setCommitID(selectedOption?.MainID?.toString() || "");
  };

  const farmerOptions = farmersList.map((farmer) => ({
    value: farmer.id,
    label: farmer.farmer_name,
    MainID: farmer.id,
    data: farmer,
  }));
  const selectedValue = selectedFarmer
    ? {
        value: selectedFarmer.id,
        label: selectedFarmer.farmer_name,
        data: selectedFarmer,
        MainID: selectedFarmer.id,
      }
    : null;

  return (
    <div className="flex flex-col">
      <label htmlFor="farmer" className="text-base font-medium mb-2">
        نام کشاورز:
      </label>

      <Select
        options={farmerOptions}
        value={selectedValue}
        onChange={handleChange}
        onInputChange={setSearchInput}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base, state) => ({
            ...base,
            backgroundColor: "inherit",
            boxShadow: state.isFocused ? "0 0 0 1px #2684FF" : "none",
            minHeight: "44px",
            borderColor: state.isFocused ? "#2684FF" : "#ccc",
            "&:hover": {
              borderColor: state.isFocused ? "#2684FF" : "#999",
            },
          }),
          option: (base, state) => ({
            ...base,
            color: "inherit",
            backgroundColor: state.isFocused
              ? "#2684FF20"
              : base.backgroundColor,
            "&:hover": {
              backgroundColor: "#2684FF40",
            },
          }),
        }}
        menuPortalTarget={document.body}
        placeholder="جستجو ..."
        noOptionsMessage={() => "کشاورزی با این مشخصات یافت نشد"}
        loadingMessage={() => "در حال بارگذاری..."}
        className="w-full my-react-select-container"
        classNamePrefix="my-react-select"
        maxMenuHeight={300}
        isClearable={true}
        isSearchable={true}
      />

      {!isEdit && commitID && (
        <div className="mt-3">
          {isLoadingBtnModal ? (
            <div className="bg-green-100 dark:bg-zink-600 p-2 rounded-md text-xs text-gray-600 dark:text-gray-300 flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              در حال دریافت داده ...
            </div>
          ) : (
            <FarmerInfoForModal selectedFarmerInfo={selectedFarmerInfo} />
          )}
        </div>
      )}
      {!isEdit && (
        <FarmerIdentityInfo
          farmerId={selectedFarmer?.farmer_id}
          cropYearId={cropyearID}
          className="mt-3"
        />
      )}
    </div>
  );
};

export default FarmerSelect;
